import React, { useState, useCallback } from 'react';
import { X, Upload, Image } from 'lucide-react';

interface ReferenceImageUploaderProps {
  onImagesChange: (images: File[]) => void;
  maxImages?: number;
  className?: string;
}

export const ReferenceImageUploader: React.FC<ReferenceImageUploaderProps> = ({
  onImagesChange,
  maxImages = 3,
  className = ''
}) => {
  const [images, setImages] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);

  const handleFileSelect = useCallback((files: FileList) => {
    const imageFiles = Array.from(files).filter(file => file.type.startsWith('image/'));
    const limitedFiles = imageFiles.slice(0, maxImages);
    
    setImages(limitedFiles);
    onImagesChange(limitedFiles);
  }, [maxImages, onImagesChange]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    if (e.dataTransfer.files) {
      handleFileSelect(e.dataTransfer.files);
    }
  }, [handleFileSelect]);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFileSelect(e.target.files);
    }
  }, [handleFileSelect]);

  const removeImage = useCallback((index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onImagesChange(newImages);
  }, [images, onImagesChange]);

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <Image className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium text-gray-700">
          Reference Images for Character Consistency
        </span>
        <span className="text-xs text-gray-500">
          (Max {maxImages})
        </span>
      </div>

      {/* Drop Zone */}
      <div
        className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-blue-400 bg-blue-50'
            : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        }`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileInput}
          className="absolute inset-0 opacity-0 cursor-pointer"
        />
        
        <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-sm text-gray-600 mb-1">
          Drop reference images here or click to select
        </p>
        <p className="text-xs text-gray-500">
          PNG, JPG, WebP up to 5MB each
        </p>
      </div>

      {/* Selected Images Preview */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 gap-3">
          {images.map((image, index) => (
            <div key={index} className="relative group">
              <img
                src={URL.createObjectURL(image)}
                alt={`Reference ${index + 1}`}
                className="w-full h-20 object-cover rounded-lg border border-gray-200"
              />
              <button
                onClick={() => removeImage(index)}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
              >
                <X className="w-3 h-3" />
              </button>
              <div className="absolute bottom-1 left-1 text-xs bg-black/50 text-white px-1 rounded">
                {Math.round(image.size / 1024)}KB
              </div>
            </div>
          ))}
        </div>
      )}

      {images.length > 0 && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-sm text-blue-700">
            <strong>{images.length} reference image{images.length > 1 ? 's' : ''} loaded</strong>
          </p>
          <p className="text-xs text-blue-600 mt-1">
            These images will help the AI match your existing icon style and character consistency.
            The AI will analyze line weight, colors, shapes, and overall artistic approach.
          </p>
        </div>
      )}
    </div>
  );
};

export default ReferenceImageUploader;